import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  CircularProgress,
  Tooltip,
  Button,
  Alert,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { customGetAPI, customAPI } from '../../services/utils/CustomAPI';

const selectDomain = (state) => state.tokenReducer.domain;

const FEATURE_LABELS = {
  attendance: 'Attendance',
  leave: 'Leave management',
  shifts: 'Shift management',
  tasks: 'Tasks & projects',
  crm: 'CRM',
  payroll: 'Payroll',
  system: 'System',
};

const CHANNEL_LABELS = { push: 'Push', bell: 'In-app', email: 'Email' };

const NotificationPreferences = () => {
  const domain = useSelector(selectDomain);

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [userEdits, setUserEdits] = useState({});

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await customGetAPI(`${domain}v1/notifications/config/`);
      setConfig(res?.responseDetails);
    } catch (e) {
      setError('Failed to load preferences. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [domain]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const getUserValue = (ev, channel) => {
    const editKey = `${channel}_enabled`;
    if (userEdits[ev.event_type]?.[editKey] !== undefined) {
      return userEdits[ev.event_type][editKey];
    }
    return ev.user?.[editKey] !== undefined ? ev.user[editKey] : true;
  };

  const isChannelLocked = (ev, channel) =>
    !ev.feature_enabled || !ev.tenant?.[`${channel}_enabled`];

  const updatePref = (event_type, channel, value) => {
    setUserEdits((prev) => ({
      ...prev,
      [event_type]: {
        ...(prev[event_type] || {}),
        [`${channel}_enabled`]: value,
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const preferences = Object.entries(userEdits).map(
        ([event_type, channels]) => ({ event_type, ...channels }),
      );
      await customAPI(
        `${domain}v1/notifications/preferences/`,
        'PUT',
        { preferences },
      );
      setSaved(true);
      setUserEdits({});
      setTimeout(() => setSaved(false), 3000);
      await loadConfig();
    } catch (e) {
      setError('Failed to save preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const hasPendingChanges = Object.keys(userEdits).length > 0;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (config && !config.tenant?.notifications_enabled) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', py: 4, px: 2 }}>
        <Alert severity="info">
          Notifications are currently disabled by your organisation.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', py: 3, px: { xs: 2, sm: 3 } }}>
      <Typography variant="h5" fontWeight={500} gutterBottom>
        My Notification Preferences
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Choose how you receive notifications. Options locked by your
        organisation cannot be changed here.
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 3 }}>
        <LockOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
        <Typography variant="caption" color="text.disabled">
          = controlled by your organisation
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {saved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Preferences saved.
        </Alert>
      )}

      {/* Column header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          pr: 2,
          mb: 0.5,
          gap: 2,
        }}
      >
        {Object.values(CHANNEL_LABELS).map((label) => (
          <Typography
            key={label}
            variant="caption"
            color="text.secondary"
            sx={{ width: 40, textAlign: 'center' }}
          >
            {label}
          </Typography>
        ))}
      </Box>

      {config?.events_by_feature &&
        Object.entries(config.events_by_feature).map(([feature, events]) => {
          const featureEnabled = config.tenant?.[`feature_${feature}`];

          return (
            <Accordion
              key={feature}
              disableGutters
              sx={{ mb: 1, border: '1px solid', borderColor: 'divider' }}
              elevation={0}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {FEATURE_LABELS[feature] || feature}
                  </Typography>
                  {!featureEnabled && (
                    <Chip
                      label="off"
                      size="small"
                      variant="outlined"
                      icon={<LockOutlinedIcon style={{ fontSize: 12 }} />}
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {events.map((ev, idx) => (
                  <React.Fragment key={ev.event_type}>
                    <Box sx={{ px: 2, py: 1.5 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 2,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2">{ev.label}</Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            {ev.description}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: 'flex',
                            gap: 0.5,
                            flexShrink: 0,
                            alignItems: 'center',
                          }}
                        >
                          {['push', 'bell', 'email'].map((ch) => {
                            const locked = isChannelLocked(ev, ch);
                            return (
                              <Tooltip
                                key={ch}
                                title={
                                  locked
                                    ? 'Controlled by your organisation'
                                    : CHANNEL_LABELS[ch]
                                }
                              >
                                <span>
                                  <Switch
                                    size="small"
                                    checked={locked ? false : getUserValue(ev, ch)}
                                    onChange={(e) =>
                                      updatePref(ev.event_type, ch, e.target.checked)
                                    }
                                    disabled={locked}
                                    icon={
                                      locked ? (
                                        <LockOutlinedIcon style={{ fontSize: 14 }} />
                                      ) : undefined
                                    }
                                  />
                                </span>
                              </Tooltip>
                            );
                          })}
                        </Box>
                      </Box>
                    </Box>
                    {idx < events.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </AccordionDetails>
            </Accordion>
          );
        })}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          mt: 3,
          pt: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          position: 'sticky',
          bottom: 0,
          bgcolor: 'background.default',
          pb: 1,
        }}
      >
        <Button
          onClick={loadConfig}
          disabled={saving || !hasPendingChanges}
          variant="outlined"
        >
          Reset
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || !hasPendingChanges}
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? 'Saving...' : 'Save preferences'}
        </Button>
      </Box>
    </Box>
  );
};

export default NotificationPreferences;
